/**
 * Provider Detection Utility
 * 
 * Detecta e valida providers de vídeo baseado em URLs ou dados
 */

/**
 * Detecta o provider baseado na URL
 * @param {string} url - URL do vídeo
 * @returns {string} Provider detectado ('vimeo', 'youtube', 'bunny', 'iframe', 'native')
 */
export function detectProviderFromUrl(url) {
  if (!url || typeof url !== 'string') {
    return 'native';
  }

  const lowerUrl = url.toLowerCase();

  // YouTube
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return 'youtube';
  }

  // Vimeo
  if (lowerUrl.includes('vimeo.com') || lowerUrl.includes('player.vimeo.com')) {
    return 'vimeo';
  }

  // Bunny CDN
  if (lowerUrl.includes('mediadelivery.net') || lowerUrl.includes('bunny')) {
    return 'bunny';
  }

  // Iframe genérico
  if (lowerUrl.includes('iframe') || lowerUrl.includes('embed')) {
    return 'iframe';
  }

  // Native/arquivo direto
  return 'native';
}

/**
 * Valida se um video ID é válido para o provider
 * @param {string} provider - Provider do vídeo
 * @param {string} videoId - ID do vídeo
 * @returns {boolean} Se o ID é válido
 */
export function validateVideoId(provider, videoId) {
  if (!provider || !videoId || typeof videoId !== 'string') {
    return false;
  }

  switch (provider) {
    case 'vimeo':
      // Vimeo IDs são números
      return /^\d+$/.test(videoId);

    case 'youtube':
      // YouTube IDs têm 11 caracteres alfanuméricos
      return /^[a-zA-Z0-9_-]{11}$/.test(videoId);

    case 'bunny':
      // Bunny IDs podem ser alfanuméricos com hífens
      return /^[a-zA-Z0-9_-]+$/.test(videoId);

    case 'native':
    case 'iframe':
      // Para native/iframe, não usamos video_id
      return false;

    default:
      return false;
  }
}

/**
 * Valida se um library ID é válido para o Bunny CDN
 * @param {string} libraryId - Library ID do Bunny
 * @returns {boolean} Se o library ID é válido
 */
export function validateBunnyLibraryId(libraryId) {
  if (!libraryId || typeof libraryId !== 'string') {
    return false;
  }

  // Bunny library IDs são alfanuméricos com hífens
  return /^[a-zA-Z0-9_-]+$/.test(libraryId);
}

/**
 * Valida se uma URL é válida para o provider
 * @param {string} provider - Provider do vídeo
 * @param {string} url - URL do vídeo
 * @returns {boolean} Se a URL é válida
 */
export function validateUrl(provider, url) {
  if (!provider || !url || typeof url !== 'string') {
    return false;
  }

  try {
    new URL(url);
  } catch {
    return false;
  }

  switch (provider) {
    case 'vimeo':
      return url.includes('vimeo.com') || url.includes('player.vimeo.com');

    case 'youtube':
      return url.includes('youtube.com') || url.includes('youtu.be');

    case 'bunny':
      return url.includes('mediadelivery.net') || url.includes('bunny');

    case 'iframe':
      return url.includes('iframe') || url.includes('embed');

    case 'native':
      // Para native, aceita qualquer URL válida
      return true;

    default:
      return false;
  }
}

/**
 * Extrai video ID de uma URL baseado no provider
 * @param {string} provider - Provider do vídeo
 * @param {string} url - URL do vídeo
 * @returns {string|null} Video ID extraído ou null
 */
export function extractVideoIdFromUrl(provider, url) {
  if (!provider || !url || typeof url !== 'string') {
    return null;
  }

  switch (provider) {
    case 'vimeo':
      return extractVimeoId(url);

    case 'youtube':
      return extractYouTubeId(url);

    case 'bunny':
      return extractBunnyIds(url);

    default:
      return null;
  }
}

/**
 * Extrai video ID do Vimeo
 * @param {string} url - URL do Vimeo
 * @returns {string|null} Video ID ou null
 */
function extractVimeoId(url) {
  const regExp = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(?:.*#|.*\/videos\/)?([0-9]+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
}

/**
 * Extrai video ID do YouTube
 * @param {string} url - URL do YouTube
 * @returns {string|null} Video ID ou null
 */
function extractYouTubeId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

/**
 * Extrai library ID e video ID do Bunny CDN
 * @param {string} url - URL do Bunny CDN
 * @returns {Object|null} {library_id, video_id} ou null
 */
function extractBunnyIds(url) {
  const regExp = /iframe\.mediadelivery\.net\/embed\/([^\/]+)\/([^\/\?]+)/;
  const match = url.match(regExp);
  return match ? {
    library_id: match[1],
    video_id: match[2]
  } : null;
}

/**
 * Detecta provider e extrai dados de uma URL
 * @param {string} url - URL do vídeo
 * @returns {Object|null} Dados extraídos ou null
 */
export function detectAndExtractFromUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const provider = detectProviderFromUrl(url);
  
  switch (provider) {
    case 'vimeo': {
      const videoId = extractVimeoId(url);
      return videoId ? { provider, video_id: videoId, original_url: url } : null;
    }

    case 'youtube': {
      const videoId = extractYouTubeId(url);
      return videoId ? { provider, video_id: videoId, original_url: url } : null;
    }

    case 'bunny': {
      const ids = extractBunnyIds(url);
      return ids ? {
        provider,
        video_id: ids.video_id,
        video_library_id: ids.library_id,
        original_url: url
      } : null;
    }

    case 'iframe':
    case 'native':
      return { provider, library_url: url, original_url: url };

    default:
      return null;
  }
}

/**
 * Valida dados completos de um MediaMapping
 * @param {Object} mediaMapping - Objeto MediaMapping
 * @returns {Object} Resultado da validação
 */
export function validateMediaMapping(mediaMapping) {
  if (!mediaMapping) {
    return { isValid: false, errors: ['MediaMapping não fornecido'] };
  }

  const errors = [];
  const { provider, video_id, video_library_id, library_url } = mediaMapping;

  // Validar provider
  if (!provider) {
    errors.push('Provider não especificado');
  } else if (!['vimeo', 'youtube', 'bunny', 'native', 'iframe'].includes(provider)) {
    errors.push(`Provider inválido: ${provider}`);
  }

  // Validar dados específicos do provider
  switch (provider) {
    case 'vimeo':
    case 'youtube':
      if (!video_id) {
        errors.push(`${provider}: video_id é obrigatório`);
      } else if (!validateVideoId(provider, video_id)) {
        errors.push(`${provider}: video_id inválido`);
      }
      break;

    case 'bunny':
      if (!video_id) {
        errors.push('bunny: video_id é obrigatório');
      } else if (!validateVideoId('bunny', video_id)) {
        errors.push('bunny: video_id inválido');
      }
      if (!video_library_id) {
        errors.push('bunny: video_library_id é obrigatório');
      } else if (!validateBunnyLibraryId(video_library_id)) {
        errors.push('bunny: video_library_id inválido');
      }
      break;

    case 'native':
    case 'iframe':
      if (!library_url) {
        errors.push(`${provider}: library_url é obrigatório`);
      } else if (!validateUrl(provider, library_url)) {
        errors.push(`${provider}: library_url inválida`);
      }
      break;
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
